/**
 * zod schemas for the rules YAML (resources, units, buildings, global
 * rules) and the `RuleSet` types the sim reads. What each key means is
 * documented in the YAML files' header comments; cross-file checks live in
 * load.ts.
 */
import { z } from 'zod';
import { HexTileTypeList } from '../hexmap/model/types';

const IdSchema = z.string().regex(/^[a-z][a-z0-9-]*$/, {
  message: 'ids must be lowercase kebab-case',
});

const ResourceAmountsSchema = z.record(IdSchema, z.number().int().nonnegative());

const ResourceDefSchema = z.object({
  name: z.string().min(1),
  /** Tile types workers gather it from in the wild; empty = only from buildings (`provides`). */
  gatheredFrom: z.array(z.enum(HexTileTypeList)),
});

const UnitActionSchema = z.enum(['gather', 'build', 'found-city', 'attack', 'heal']);

/**
 * Longest a shot may fly, or a ranged attacker wait to reload, in sim ticks
 * (100 ms): the wire validator (net/protocol.ts) rejects more, so the loader
 * does too.
 */
export const MAX_SHOT_TICKS = 1000;

/** How a projectile flies (and is drawn): its flight time comes from rules.yaml `shots.flight`. */
const ShotKindSchema = z.enum(['tracer', 'arrow', 'shell', 'missile', 'beam']);

/**
 * Hit chance of a shot by distance: `base` at 1 hex, plus `perHex` for each
 * hex further, never below `min` (nor above 1).
 */
const AccuracySchema = z.object({
  base: z.number().min(0).max(1),
  perHex: z.number(),
  min: z.number().min(0).max(1).default(0),
});

/**
 * A ranged attacker's shots (range 2 or more; melee deals continuous
 * damage): one every `fireInterval` seconds, each hitting for strength ×
 * `fireInterval` with `accuracy`'s chance. Left out: rules.yaml `shots`
 * defaults (`fireInterval`, `accuracy`) and an arrow.
 */
const ShotDefSchema = z.object({
  kind: ShotKindSchema,
  fireInterval: z.number().positive().optional(),
  accuracy: AccuracySchema.optional(),
});

/**
 * One way a unit of this type can be promoted in place (the `promote`
 * command, sim/promotion.ts): into `to` for `cost`, while it stands in the
 * territory of an own completed city whose territory holds a completed `at`
 * building of at least that level (omitted: any own city will do).
 */
const UnitUpgradeSchema = z.object({
  to: IdSchema,
  cost: ResourceAmountsSchema,
  at: z
    .object({
      building: IdSchema,
      level: z.number().int().positive(),
    })
    .optional(),
});

/** How the UI shows a unit type (see rules/ui.ts); every key optional. */
const UnitUiSchema = z.object({
  /** Short tag in the units roster ("Wk"); omitted = the id's first two letters. */
  glyph: z.string().min(1).max(3).optional(),
  /** Plural name ("Cavalry"); omitted = `name` + "s" ("Spy" → "Spies"). */
  plural: z.string().min(1).optional(),
  /** Roster position, ascending; omitted = after those with one, in file order. */
  order: z.number().int().optional(),
});

const UnitDefSchema = z.object({
  name: z.string().min(1),
  /** Display hints (roster glyph, plural, order). */
  ui: UnitUiSchema.optional(),
  cost: ResourceAmountsSchema,
  speedMultiplier: z.number().positive(),
  visionRadius: z.number().int().nonnegative(),
  terrain: z.array(z.enum(HexTileTypeList)).min(1),
  actions: z.array(UnitActionSchema),
  hp: z.number().int().positive(),
  /** Damage per second to one enemy in range (needs the `attack` action). */
  strength: z.number().nonnegative(),
  /** Attack (or healing) reach in hexes; omitted = 1 (own and adjacent tiles). */
  range: z.number().int().positive().default(1),
  /**
   * Hp per second a healer restores to one own unit in `range` (needs the
   * `heal` action; see systems/heal.ts).
   */
  healRate: z.number().positive().optional(),
  /** Which units a healer mends: `organic` (people, horses) or `mechanical` ones. */
  heals: z.enum(['organic', 'mechanical']).optional(),
  /** A machine (rams, vehicles, robots): mended by `heals: mechanical` healers, not organic ones. */
  mechanical: z.boolean().optional(),
  /** Its shots when `range` is 2 or more (see ShotDefSchema). */
  shot: ShotDefSchema.optional(),
  /**
   * Most a gatherer carries: gathering fills its load, which it must haul to
   * an own city to deposit. Omitted = what it gathers goes straight to a city.
   */
  carryCapacity: z.number().positive().optional(),
  /** Multiplier on everything it gathers (needs the `gather` action); omitted = 1. */
  gatherMultiplier: z.number().positive().optional(),
  /**
   * An army unit (needs the `attack` action and some strength, or the `heal`
   * action): it may be stored in a barracks (or whichever building `stores`
   * it), pays army upkeep and never stands on a city tile.
   */
  military: z.boolean().optional(),
  /**
   * Seconds it takes to train at its city's training building (the store
   * that would hold it: a barracks, a factory; see sim/systems/training.ts),
   * paid when queued. Needs `military`. Omitted = it appears at once.
   */
  trainTime: z.number().positive().optional(),
  /**
   * Resources per second the unit costs while alive, paid from its owner's
   * cities (see systems/upkeep.ts).
   */
  upkeep: z.record(IdSchema, z.number().nonnegative()).optional(),
  /** Damage multiplier against these unit types (e.g. warriors vs cavalry). */
  bonusVs: z.record(IdSchema, z.number().positive()).optional(),
  /** Damage multiplier against buildings (artillery: ×2); omitted = 1. */
  bonusVsBuildings: z.number().positive().optional(),
  /** What it may attack; omitted = any. */
  targets: z.enum(['any', 'units', 'buildings']).optional(),
  /** Automatic behaviour units of this type start with (and can be set back to). */
  automate: z.enum(['explore', 'work']).optional(),
  /** How many of this unit each completed city allows a player; omitted = unlimited. */
  perCity: z.number().int().positive().optional(),
  /** Extra cap each of those cities adds per level above 1 (needs `perCity`). */
  perCityLevel: z.number().int().nonnegative().optional(),
  /**
   * Counts as this unit type: it shares that type's cap (its units and these
   * together) instead of having a `perCity` of its own, and builds what that
   * type builds (`builtBy`) when it has the `build` action. A labourer counts
   * as a worker.
   */
  capAs: IdSchema.optional(),
  /**
   * Share of `cost` added per city the player has beyond the first, counting
   * unfinished cities and living units of this type (settlers on their way to
   * found one): 0.5 = ×1.5 with 2, ×2 with 3. Omitted = a flat cost. Only for
   * units with the `found-city` action (the loader rejects it elsewhere).
   */
  costPerCity: z.number().nonnegative().optional(),
  /** Only trainable at a city whose territory holds a completed `building` of at least `level`. */
  requires: z
    .object({
      building: IdSchema,
      level: z.number().int().positive(),
    })
    .optional(),
  /** What a unit of this type can be promoted into (see UnitUpgradeSchema). */
  upgrades: z.array(UnitUpgradeSchema).optional(),
  /**
   * Unseen by other players until exposed (see sim/spies.ts): it can't be
   * targeted, blocks nobody and holds no tile.
   */
  stealth: z.boolean().optional(),
  /**
   * The unit type it passes for when it blends into a rival city (the
   * `blend` command; needs `stealth`): that city's owner sees one of these.
   */
  disguiseAs: IdSchema.optional(),
});

/**
 * One upgrade step. Stats left out keep the previous level's value; `cost`
 * is paid when upgrading to this level.
 */
const BuildingLevelSchema = z.object({
  cost: ResourceAmountsSchema,
  /**
   * Seconds the upgrade to this level takes once paid. Required, so no level
   * is accidentally instant; the building keeps its old stats until it ends.
   */
  upgradeTime: z.number().min(0.001),
  /** Display name of this level (a city's era: "Industrial"; a farm's "Mechanised"). */
  title: z.string().min(1).optional(),
  /**
   * Lowest level of the city whose territory holds the building for an
   * upgrade to this level (placed buildings also can't pass their city's
   * level at all, so this only matters when it is higher than this level).
   */
  cityLevel: z.number().int().positive().optional(),
  hp: z.number().int().positive().optional(),
  territoryRadius: z.number().int().positive().optional(),
  visionRadius: z.number().int().nonnegative().optional(),
  /** Extra gathering in this city's territory (0.1 = +10%). */
  gatherBonus: z.number().nonnegative().optional(),
  /** Replaces `provides.rate` for workers on this building. */
  rate: z.number().positive().optional(),
  /** Replaces `workerSlots`. */
  workerSlots: z.number().int().positive().optional(),
  /** Replaces `capacity`. */
  capacity: z.number().int().positive().optional(),
  /** Replaces `trainSlots`. */
  trainSlots: z.number().int().positive().optional(),
  cityHp: z.number().int().nonnegative().optional(),
  cityStrength: z.number().nonnegative().optional(),
  /** Replaces `strength` (damage per second it deals). */
  strength: z.number().nonnegative().optional(),
  /** Replaces `range`. */
  range: z.number().int().positive().optional(),
  /** Replaces `shot`. */
  shot: ShotDefSchema.optional(),
  /** Replaces `watchRange`. */
  watchRange: z.number().int().positive().optional(),
  /** Replaces `watchRadius`. */
  watchRadius: z.number().int().nonnegative().optional(),
  /** Replaces `cityVision`. */
  cityVision: z.number().int().nonnegative().optional(),
  /**
   * Share of its max hp the building regains each second while mending,
   * replacing `pacing.cityRepair` / `pacing.buildingRepair` from this level.
   */
  repair: z.number().nonnegative().optional(),
});

const BuildingDefSchema = z.object({
  name: z.string().min(1),
  /** Display name of level 1 (a city's first era: "Settlement"); omitted = `name`. */
  title: z.string().min(1).optional(),
  builtBy: IdSchema,
  consumesBuilder: z.boolean(),
  cost: ResourceAmountsSchema.optional(),
  buildTime: z.number().positive(),
  hp: z.number().int().positive(),
  visionRadius: z.number().int().nonnegative(),
  territoryRadius: z.number().int().positive().optional(),
  /**
   * Least distance from another city (any owner, sites included) at which a
   * city may be founded; read from the city type (see sim/placement.ts
   * `minCityDistance`). Omitted = 5.
   */
  minDistanceToCity: z.number().int().positive().default(5),
  spawns: z.array(IdSchema).optional(),
  /**
   * Own units anywhere in this (completed) building's territory can't be hurt,
   * unless they attack that tick. Needs `territoryRadius`.
   */
  garrison: z.boolean().optional(),
  /** Damage per second a completed building deals to one enemy in range; omitted = none. */
  strength: z.number().nonnegative().optional(),
  /** Attack reach in hexes; omitted = 1. */
  range: z.number().int().positive().optional(),
  /** Its shots while its `range` is 2 or more (see ShotDefSchema). */
  shot: ShotDefSchema.optional(),
  /** Extra max hp the city whose territory this (completed) building stands in gets. */
  cityHp: z.number().int().nonnegative().optional(),
  /** Extra strength that city gets. */
  cityStrength: z.number().nonnegative().optional(),
  /** Extra vision radius the city whose territory this (completed) building stands in gets. */
  cityVision: z.number().int().nonnegative().optional(),
  /**
   * A lookout (the watchtower): its owner may point it at one tile within
   * `watchRange` hexes of it (the `watch` command) and, while it stands
   * completed, sees `watchRadius` around that tile (sim/vision.ts).
   */
  watchRange: z.number().int().positive().optional(),
  /** Vision radius around the watch spot (needs `watchRange`). */
  watchRadius: z.number().int().nonnegative().optional(),
  /** How many of this building (sites included) each city's territory may hold. */
  maxPerCity: z.number().int().positive().optional(),
  placement: z
    .object({
      on: z.array(z.enum(HexTileTypeList)).min(1),
      within: z.literal('territory'),
      /** Lowest level the city whose territory holds the tile must have (an era unlock). */
      cityLevel: z.number().int().positive().optional(),
    })
    .optional(),
  /** A resource workers on this (completed) building gather without it running out. */
  provides: z
    .object({
      resource: IdSchema,
      /** Multiplier on `pacing.baseGatherRate` for each worker here. */
      rate: z.number().positive().default(1),
    })
    .optional(),
  /** How many workers can work inside a `provides` building at once; omitted = 1. */
  workerSlots: z.number().int().positive().optional(),
  /**
   * Spreading over nearby land of its `placement.on` type in its city's
   * territory (see sim/footprint.ts): extra tiles it may cover at level 1, 2,
   * … (the last entry holds for higher levels), each adding worker slots
   * (a farm or forestry) or storage `capacity` (a barracks).
   */
  growth: z
    .object({
      extraTilesByLevel: z.array(z.number().int().nonnegative()).min(1),
      slotsPerExtraTile: z.number().int().nonnegative().optional(),
      capacityPerExtraTile: z.number().int().nonnegative().optional(),
    })
    .optional(),
  /**
   * Unit types that go inside this (completed, own) building when they stop
   * on its tile, any number of them (a city shelters its workers and settlers).
   */
  accepts: z.array(IdSchema).optional(),
  /** Military units this (completed) building stores (a barracks); omitted = none. */
  capacity: z.number().int().positive().optional(),
  /**
   * The military unit types it stores (needs `capacity`): soldiers in a
   * barracks, vehicles in a factory. Omitted = every military unit.
   */
  stores: z.array(IdSchema).min(1).optional(),
  /**
   * Units with a `trainTime` that train here at once (needs `capacity`); the
   * rest of its city's queue for it waits. Omitted = it trains none, and
   * its units appear at once.
   */
  trainSlots: z.number().int().positive().optional(),
  /** Upgrades past level 1, in order: `levels[0]` is level 2. */
  levels: z.array(BuildingLevelSchema).optional(),
});

const TimeoutResolverSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('resource-score'),
    /** Whether living military units count in the score at their training cost. */
    army: z.boolean().default(false),
    /**
     * What each resource held in city stock scores (1: the same as spending
     * it). Below 1 rewards building and training over hoarding.
     */
    stockWeight: z.number().nonnegative().default(1),
    /**
     * What a city's levels score, as a share of their cost (1: at cost).
     * Below 1 keeps city levels, the biggest sink, from dominating the score.
     */
    cityLevelWeight: z.number().nonnegative().default(1),
  }),
]);

const GameRulesSchema = z.object({
  pacing: z.object({
    baseSpeed: z.number().positive(),
    baseGatherRate: z.number().positive(),
    tileYield: z.number().positive(),
    /** Share of a destroyed city's stock its attacker carries off (the rest is lost). */
    plunderShare: z.number().min(0).max(1),
    /**
     * Share of a destroyed rival building's cost (and its levels' costs) the
     * attacker who did the most damage to it that tick gains, like plunder.
     */
    razeShare: z.number().min(0).max(1),
    /** Share of a site's cost refunded when its owner cancels it (`cancel` command). */
    cancelRefund: z.number().min(0).max(1),
    /** Ticks a hauling worker spends unloading at a city. */
    depositTicks: z.number().int().nonnegative(),
    /** Hp/s each military unit loses while its player has no food for upkeep … */
    attrition: z.number().nonnegative(),
    /** … once they have been unable to pay it for this many seconds. */
    starvationGrace: z.number().nonnegative(),
    /**
     * Share each military unit adds to its player's upkeep: effective upkeep
     * = base × (1 + armyUpkeep × military units).
     */
    armyUpkeep: z.number().nonnegative(),
    /** Share of damage units standing in their owner's territory don't take (0.25 = 25% less). */
    homeDefence: z.number().min(0).max(1),
    /**
     * Last stand: a player's only completed city gets this share more max hp
     * (current hp rises by the same amount) …
     */
    lastStandHp: z.number().nonnegative(),
    /** … and its fire is multiplied by this. */
    lastStandFire: z.number().positive(),
    /**
     * Seconds a player can't `expose` again after exposing something that
     * wasn't a disguised spy in their territory.
     */
    exposeCooldown: z.number().nonnegative(),
    /**
     * Seconds an undisguised spy may stay in a rival completed city's
     * territory before it's caught (exposed).
     */
    spyGrace: z.number().positive(),
    /**
     * Repair: a completed building that hasn't taken damage for repairDelay
     * seconds regains a share of its max hp per second: cityRepair for a
     * building that holds stock (a city), buildingRepair for any other,
     * unless its level sets `repair`.
     */
    repairDelay: z.number().nonnegative(),
    cityRepair: z.number().nonnegative(),
    buildingRepair: z.number().nonnegative(),
  }),
  /**
   * Ranged attacks (range 2+) fire discrete shots that hit or miss (see
   * systems/combat.ts); these are the defaults and the flight times.
   */
  shots: z.object({
    /** Seconds between shots of a ranged attacker without its own `shot.fireInterval`. */
    fireInterval: z.number().positive(),
    /** Hit chance of one without its own `shot.accuracy`. */
    accuracy: AccuracySchema,
    /** Ticks a shot of each kind flies: `base` plus `perHex` per hex of distance (at least 1). */
    flight: z.record(
      ShotKindSchema,
      z.object({ base: z.number().nonnegative(), perHex: z.number().nonnegative() })
    ),
    /** Ticks a shot stays in state after its impact (for the client to draw it landing). */
    linger: z.number().int().nonnegative(),
    /**
     * How much a moving target spoils a shot of each kind: a unit that is
     * walking is hit with the chance × (1 − movingPenalty × its speed ÷ the
     * fastest unit type's speed). Still units and buildings are unaffected.
     */
    movingPenalty: z.record(ShotKindSchema, z.number().min(0).max(1)),
  }),
  /** Units rank up from xp (damage dealt) or training; see veterancy.ts. */
  veterancy: z.object({
    xpPerDamage: z.number().nonnegative(),
    /** Xp per damage dealt to a building; `xpPerDamage` when absent. */
    xpPerBuildingDamage: z.number().nonnegative().optional(),
    /** Xp a healer earns per hp it restores; omitted = 0 (healers never rank up by healing). */
    xpPerHeal: z.number().nonnegative().optional(),
    /** Xp needed for rank 1, 2, …; ascending. */
    ranks: z.array(z.number().positive()),
    /** Share of the type's hp / strength added per rank. */
    hpPerRank: z.number().nonnegative(),
    strengthPerRank: z.number().nonnegative(),
    /** Cost of training a rank, times the rank bought. */
    trainCost: ResourceAmountsSchema,
  }),
  match: z.object({
    duration: z.number().positive(),
    victory: z.literal('last-player-standing'),
    timeout: TimeoutResolverSchema,
  }),
  player: z.object({
    startingResources: ResourceAmountsSchema,
    startingUnits: z.array(IdSchema),
    startingBuildings: z.array(IdSchema),
  }),
});

const ResourceDefMapSchema = z.record(IdSchema, ResourceDefSchema);
const UnitDefMapSchema = z.record(IdSchema, UnitDefSchema);
const BuildingDefMapSchema = z.record(IdSchema, BuildingDefSchema);

type ResourceDef = z.infer<typeof ResourceDefSchema>;
type UnitAction = z.infer<typeof UnitActionSchema>;
type UnitDef = z.infer<typeof UnitDefSchema>;
type UnitUpgradeDef = z.infer<typeof UnitUpgradeSchema>;
type BuildingDef = z.infer<typeof BuildingDefSchema>;
type BuildingLevelDef = z.infer<typeof BuildingLevelSchema>;
type ShotKind = z.infer<typeof ShotKindSchema>;
type ShotDef = z.infer<typeof ShotDefSchema>;
type AccuracyDef = z.infer<typeof AccuracySchema>;
type ShotsDef = GameRules['shots'];
type TimeoutResolver = z.infer<typeof TimeoutResolverSchema>;
type GameRules = z.infer<typeof GameRulesSchema>;
type PacingDef = GameRules['pacing'];
type MatchDef = GameRules['match'];
type VeterancyDef = GameRules['veterancy'];
type PlayerSetup = GameRules['player'];

interface RuleSet {
  resources: Record<string, ResourceDef>;
  units: Record<string, UnitDef>;
  buildings: Record<string, BuildingDef>;
  pacing: PacingDef;
  shots: ShotsDef;
  veterancy: VeterancyDef;
  match: MatchDef;
  player: PlayerSetup;
}

export type {
  AccuracyDef,
  BuildingDef,
  BuildingLevelDef,
  MatchDef,
  PacingDef,
  PlayerSetup,
  ResourceDef,
  RuleSet,
  ShotDef,
  ShotKind,
  ShotsDef,
  TimeoutResolver,
  UnitAction,
  UnitDef,
  UnitUpgradeDef,
  VeterancyDef,
};

export {
  BuildingDefMapSchema,
  BuildingDefSchema,
  BuildingLevelSchema,
  GameRulesSchema,
  ResourceDefMapSchema,
  ResourceDefSchema,
  ShotKindSchema,
  TimeoutResolverSchema,
  UnitActionSchema,
  UnitDefMapSchema,
  UnitDefSchema,
};
